// components/gtm-loader.jsx — Google Tag Manager, loaded deliberately late.
//
// GTM has no rendering role, but its container (~320KB, with GA4 and Ads
// tags configured) costs ~1.7s of main-thread time on a throttled mobile
// CPU. next/script's "lazyOnload" (used in an earlier pass) only waits for
// window 'load', which Lighthouse still measures well past — so GTM kept
// landing inside the trace and dragging mobile TBT/Performance down.
//
// Now it waits for the visitor's first interaction (scroll, pointer, key,
// touch) — the point where a real user has shown up and the page is
// already painted — with a fallback timer so a visitor who reads without
// interacting is still counted. 'gtm.start' is stamped at page load, so
// GTM's own timing for the page view stays accurate.

'use client';

import { useEffect } from 'react';

const TRIGGERS = ['scroll', 'pointerdown', 'keydown', 'touchstart', 'mousemove'];
const FALLBACK_MS = 10_000;

export default function GTMLoader({ gtmId }) {
  useEffect(() => {
    // Guard against a second run (React dev StrictMode re-mounts effects).
    if (!window.__gtmQueued) {
      window.__gtmQueued = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    }

    let timer;
    const load = () => {
      TRIGGERS.forEach((t) => window.removeEventListener(t, load));
      clearTimeout(timer);
      if (document.getElementById('gtm-src')) return;
      const s = document.createElement('script');
      s.id = 'gtm-src';
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      document.head.appendChild(s);
    };

    TRIGGERS.forEach((t) => window.addEventListener(t, load, { once: true, passive: true }));
    timer = setTimeout(load, FALLBACK_MS);
    return () => {
      TRIGGERS.forEach((t) => window.removeEventListener(t, load));
      clearTimeout(timer);
    };
  }, [gtmId]);

  return null;
}
